import { DirectionKindEnum } from '@zaoblako/shared/modules/catalog/enums/direction.kind.enum';
import { DirectionKindEnumI18n } from '@zaoblako/shared/modules/catalog/i18n/direction.kind.enum.i18n';
import { DirectionTypeEnum } from '@zaoblako/shared/modules/catalog/enums/direction.type.enum';
import { DirectionTypeEnumI18n } from '@zaoblako/shared/modules/catalog/i18n/direction.type.enum.i18n';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { BalanceSheetSourceEnum } from '@zaoblako/shared/modules/catalog/enums/balance-sheet-source.enum';
import { BalanceSheetSourceEnumI18n } from '@zaoblako/shared/modules/catalog/i18n/balance-sheet-source-enum.i18n';
import { hexColorSchema } from '@zaoblako/shared/modules/serde/schemas/hex-color.schema';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import {
  DirectionBalanceSheetSettingsFormDto,
  type DirectionBalanceSheetSettingsFormInterface,
} from '@zaoblako/shared/modules/catalog/dtos/direction/direction-balance-sheet-settings-form.dto';
import type { AccountingAccountFindInterface } from '@zaoblako/shared/modules/catalog/dtos/accounting-account/accounting-account-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { ClassificationCategoryFindInterface } from '@zaoblako/shared/modules/finance/dtos/classification/classification-category-find.dto';

import * as yup from 'yup';

/** Direction create and update requests body */
export interface DirectionFormInterface {
  /** Display name */
  name: string;
  /** Direction kind */
  kind: DirectionKindEnum;
  /** Direction type */
  type: DirectionTypeEnum;
  /** Special code of this direction for Atrium CRM (using by some reports) */
  atriumCode?: string;
  /** Direction color (using by some reports) */
  color?: string;
  /** Accounting account number (using by some reports) */
  accountingAccount?: AccountingAccountFindInterface;
  /** Balance sheet source (using by some reports) */
  balanceSheetSource?: BalanceSheetSourceEnum;
  /** Parent direction */
  parent?: DirectionFindInterface;
  /** Generatl direction (for plans) */
  generalDirection?: DirectionFindInterface;
  /** Users whose has access to direction in budget requests */
  users?: UserFindInterface[];
  /** User groups that have access to this direction */
  userGroups?: UserGroupFindInterface[];
  /** Financial classification categories for this direction */
  classification?: ClassificationCategoryFindInterface[];
  /** Balance sheet settings for direction (dynamic field) */
  balanceSheetSettings?: DirectionBalanceSheetSettingsFormInterface[];
}

/** Direction create and update requests body - DTO schema */
export const DirectionFormDto = SerdeDtoClass.create<DirectionFormInterface>(CatalogDtosKeysEnum.DIRECTION_FORM_DTO, {
  name: {
    label: 'Название',
    serializer: yup.string(),
  },
  kind: {
    label: 'Вид',
    optional: true,
    serializer: commonEnumValidator(DirectionKindEnum, DirectionKindEnumI18n),
  },
  type: {
    label: 'Тип',
    optional: true,
    serializer: commonEnumValidator(DirectionTypeEnum, DirectionTypeEnumI18n),
  },
  atriumCode: {
    label: 'Код для "Атриума"',
    optional: true,
    serializer: yup.string(),
  },
  color: {
    optional: true,
    label: 'Цвет',
    serializer: hexColorSchema,
  },
  accountingAccount: {
    optional: true,
    label: 'Бухгалтерский счет',
    serializer: entityLikeSchema,
  },
  balanceSheetSource: {
    optional: true,
    label: 'Источник',
    serializer: commonEnumValidator(BalanceSheetSourceEnum, BalanceSheetSourceEnumI18n),
  },
  parent: {
    label: 'Группа',
    optional: true,
    checkers: [
      CatalogCheckersEnum.DIRECTION_ASSERT_EXISTS_BY_DTO,
      CatalogCheckersEnum.CHECK_PARENT_DIRECTION_BY_DTO,
    ],
    serializer: entityLikeSchema,
  },
  generalDirection: {
    label: 'Рассчетная БС',
    optional: true,
    checkers: [
      CatalogCheckersEnum.DIRECTION_ASSERT_EXISTS_BY_DTO,
      CatalogCheckersEnum.CHECK_GENERAL_DIRECTION_CIRCULAR_DEPENDENCY_BY_DTO,
    ],
    serializer: entityLikeSchema,
  },
  users: {
    label: 'Пользователи',
    optional: true,
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
    serializer: yup.array(entityLikeSchema),
  },
  userGroups: {
    label: 'Группы пользователей',
    optional: true,
    checkers: [SecurityBackendValidatorsEnum.USER_GROUP_ASSERT_EXISTS_BY_DTO],
    serializer: yup.array(entityLikeSchema),
  },
  classification: {
    optional: true,
    label: 'Финансовая классификация',
    serializer: yup.array(entityLikeSchema),
  },
  balanceSheetSettings: {
    optional: true,
    label: 'Настройки для ОСВ',
    nested: DirectionBalanceSheetSettingsFormDto,
    serializer: (_, nested) => yup.array(nested),
  },
});
